import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import PageHeader from "@/components/common/PageHeader";
import { supportService } from "@/services/support.service";
import type { SupportTicketListItem } from "@/types";

const STATUS_OPTIONS = ["OPEN", "IN_PROGRESS", "RESOLVED", "CLOSED"] as const;
const PRIORITY_OPTIONS = ["LOW", "MEDIUM", "HIGH", "URGENT"] as const;

function formatDueDate(value?: string | null) {
  return value ? new Date(value).toLocaleString() : "—";
}

function slaBadgeClass(status?: string) {
  switch (status) {
    case "escalated":
      return "bg-red-100 text-red-700";
    case "breached":
      return "bg-amber-100 text-amber-700";
    case "due_soon":
      return "bg-blue-100 text-blue-700";
    default:
      return "bg-green-100 text-green-700";
  }
}

function TicketRow({ ticket }: { ticket: SupportTicketListItem }) {
  return (
    <tr className="border-b border-slate-100 last:border-none">
      <td className="px-4 py-3 text-sm font-semibold text-slate-900">
        <Link to={`/support/${ticket.id}`} className="hover:text-slate-700">
          {ticket.subject}
        </Link>
        <p className="text-xs text-slate-500">#{ticket.ticketNumber}</p>
      </td>
      <td className="px-4 py-3 text-sm text-slate-600">
        {ticket.tenant?.name || "External"}
      </td>
      <td className="px-4 py-3 text-sm text-slate-600">{ticket.status}</td>
      <td className="px-4 py-3 text-sm text-slate-600">{ticket.priority}</td>
      <td className="px-4 py-3 text-sm text-slate-600">
        <span
          className={`inline-flex rounded-full px-2 py-1 text-xs font-semibold ${slaBadgeClass(ticket.slaStatus)}`}
        >
          {ticket.slaStatus || "on_track"}
        </span>
      </td>
      <td className="px-4 py-3 text-xs text-slate-500">
        {formatDueDate(ticket.firstResponseDueAt)}
      </td>
      <td className="px-4 py-3 text-xs text-slate-500">
        {formatDueDate(ticket.resolutionDueAt)}
      </td>
      <td className="px-4 py-3 text-xs text-slate-500">
        {ticket.assignedTo
          ? `${ticket.assignedTo.firstName} ${ticket.assignedTo.lastName ?? ""}`
          : "Unassigned"}
      </td>
      <td className="px-4 py-3 text-right text-xs text-slate-500">
        {new Date(ticket.createdAt).toLocaleDateString()}
      </td>
    </tr>
  );
}

function TicketListPage() {
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState<string>("");
  const [priority, setPriority] = useState<string>("");
  const [page, setPage] = useState(1);
  const limit = 10;

  const queryParams = useMemo(
    () => ({
      page,
      limit,
      search: search.trim() || undefined,
      status: status || undefined,
      priority: priority || undefined,
    }),
    [limit, page, priority, search, status],
  );

  const { data, isLoading } = useQuery({
    queryKey: ["admin-support-tickets", queryParams],
    queryFn: () => supportService.listTickets(queryParams),
  });

  return (
    <div className="space-y-6">
      <PageHeader
        title="Support"
        subtitle="Review tenant issues and resolve tickets."
      />

      <div className="flex flex-wrap items-center gap-3">
        <input
          value={search}
          onChange={(event) => {
            setSearch(event.target.value);
            setPage(1);
          }}
          placeholder="Search by subject, ticket number, or email"
          className="h-10 flex-1 rounded-xl border border-slate-200 px-4 text-sm focus:border-slate-400 focus:outline-none"
        />
        <select
          value={status}
          onChange={(event) => {
            setStatus(event.target.value);
            setPage(1);
          }}
          className="h-10 rounded-xl border border-slate-200 bg-white px-3 text-sm text-slate-700"
        >
          <option value="">All statuses</option>
          {STATUS_OPTIONS.map((item) => (
            <option key={item} value={item}>
              {item}
            </option>
          ))}
        </select>
        <select
          value={priority}
          onChange={(event) => {
            setPriority(event.target.value);
            setPage(1);
          }}
          className="h-10 rounded-xl border border-slate-200 bg-white px-3 text-sm text-slate-700"
        >
          <option value="">All priorities</option>
          {PRIORITY_OPTIONS.map((item) => (
            <option key={item} value={item}>
              {item}
            </option>
          ))}
        </select>
      </div>

      <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
        <table className="w-full text-left">
          <thead className="bg-slate-50 text-xs uppercase tracking-[0.2em] text-slate-400">
            <tr>
              <th className="px-4 py-3">Ticket</th>
              <th className="px-4 py-3">Tenant</th>
              <th className="px-4 py-3">Status</th>
              <th className="px-4 py-3">Priority</th>
                  <th className="px-4 py-3">SLA</th>
                  <th className="px-4 py-3">First Response Due</th>
                  <th className="px-4 py-3">Resolution Due</th>
              <th className="px-4 py-3">Assignee</th>
              <th className="px-4 py-3 text-right">Created</th>
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <tr>
                <td className="px-4 py-6 text-sm text-slate-500" colSpan={9}>
                  Loading tickets...
                </td>
              </tr>
            ) : data?.data.length ? (
              data.data.map((ticket) => (
                <TicketRow key={ticket.id} ticket={ticket} />
              ))
            ) : (
              <tr>
                <td className="px-4 py-6 text-sm text-slate-500" colSpan={9}>
                  No tickets found.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="flex items-center justify-between text-sm text-slate-500">
        <span>
          Page {data?.meta.page ?? page} of {data?.meta.totalPages ?? 1}
        </span>
        <div className="flex items-center gap-2">
          <button
            type="button"
            disabled={page <= 1}
            onClick={() => setPage((value) => Math.max(1, value - 1))}
            className="rounded-lg border border-slate-200 px-3 py-1 disabled:cursor-not-allowed disabled:opacity-50"
          >
            Previous
          </button>
          <button
            type="button"
            disabled={Boolean(data) && page >= (data?.meta.totalPages ?? 1)}
            onClick={() => setPage((value) => value + 1)}
            className="rounded-lg border border-slate-200 px-3 py-1 disabled:cursor-not-allowed disabled:opacity-50"
          >
            Next
          </button>
        </div>
      </div>
    </div>
  );
}

export default TicketListPage;
